import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Section from '../layouts/showcaseLayouts/Section/Section';
import Breadcrumbs, { BreadcrumbItem } from '../shared/Breadcrumbs/Breadcrumbs';
import SectionHeader from '../layouts/showcaseLayouts/Section/SectionHeader/SectionHeader';
import SectionBody from '../layouts/showcaseLayouts/Section/SectionBody/SectionBody';
import SectionBodyGrid from '../layouts/showcaseLayouts/Section/SectionBody/SectionBodyGrid/SectionBodyGrid';
import Filter from '../showcase/Filter/Filter';
import MobileFilter from '../shared/MobileFilter/MobileFilter';
import ProductCardList from '../showcase/ProductCardList/ProductCardList';
import Placeholder from '../UI/Placeholder/Placeholder';
import NotFound from '../pages/showcasePages/NotFound/NotFound';
import { RootState } from '../../store/store';
import { PATHS } from '../../constants/routes';
import { NO_PRODUCTS } from '../../constants/messages';
import { NO_PRODUCTS_MESSAGE } from '../../constants/messages';
import useFilterByFillerType from '../../hooks/useFilterByFillerType';
import useFilterByFabricType from '../../hooks/useFilterByFabricType';
import { Category } from '../../types/common';
import useIsLTE650 from '../../hooks/useIsLTE650';
import { normalizeString, splitToTokensNormalized, scoreFieldsForSearch } from '../../utils/searchHelpers';
import classes from './CatalogPage.module.css';

const FILTER_QUERY_KEY = 'typesOfFiller';
const FILTER_QUERY_KEY_FABRIC = 'typesOfFabric';
const GROUP_QUERY_KEY = 'groupOption';
const SEARCH_QUERY_KEY = 'q';
const SECRET_MIN_PRICE_QUERY_KEY = 'campaign'; // имя параметра в URL
const FULL_PRICE_QUERY_KEY = 'fullPrice';
const SECRET_MIN_PRICE_MAP: Record<string, number> = {
    premium: 29000, // кодовое слово "premium" означает порог 29000
    promo: 2000, // кодовое слово "promo" означает порог 2000
};

type ProductItem = RootState['product']['products'][number];
type SeasonFilter = 'all' | 'seasonless' | 'summer' | 'autumn' | 'winter' | 'spring';
type SelectedFilterChip = { key: string; label: string; onRemove: () => void };

const seasonLabelMap: Record<SeasonFilter, string> = {
    all: 'Все сезоны',
    seasonless: 'Всесезонный',
    summer: 'Летний',
    autumn: 'Осенний',
    winter: 'Зимний',
    spring: 'Весенний',
};

const normalizeKey = (v: unknown) => (v == null ? '' : String(v)).trim().toLowerCase();
const getFillerKey = (p: any) => {
    const ft = p?.fillerType;
    if (ft == null) return '';
    if (typeof ft === 'string') return normalizeKey(ft);
    return normalizeKey(ft?.url ?? ft?.slug ?? p?.fillerTypeUrl ?? p?.fillerTypeSlug ?? ft?.id ?? p?.fillerTypeId);
};
const getFabricKey = (p: any) => {
    const ft = p?.fabricType;
    if (ft == null) return '';
    if (typeof ft === 'string') return normalizeKey(ft);
    return normalizeKey(ft?.url ?? ft?.slug ?? p?.fabricTypeUrl ?? p?.fabricTypeSlug ?? ft?.id ?? p?.fabricTypeId);
};

const checkboxItemsFromArray = (items: { id: string; name: string }[]) => items.map((item) => [normalizeKey(item.id), item.name] as const);

const CatalogPage: React.FC = () => {
    const { url } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const isMobile = useIsLTE650(650);
    const isMobileFilter = useIsLTE650(975);

    const { products, error } = useSelector((state: RootState) => state.product);
    const { categories } = useSelector((state: RootState) => state.category);
    const { typesOfFiller } = useSelector((state: RootState) => state.fillerType);
    const { typesOfFabric } = useSelector((state: RootState) => state.fabricType);

    const isDiscountsRoute = url === 'discounts';

    const category = useMemo(
        () => (url && !isDiscountsRoute ? (categories.find((c) => c.url === url) as Category | undefined) : undefined),
        [categories, url, isDiscountsRoute],
    );
    const notFound = !!url && !category && !isDiscountsRoute;

    const sourceProducts = useMemo<ProductItem[]>(() => {
        if (isDiscountsRoute) {
            return products.filter((p) => p?.promoType != null);
        }
        if (url) return products.filter((p) => p.category.url === url);
        return [...products];
    }, [products, url, isDiscountsRoute]);
    const hasProducts = sourceProducts.length > 0;

    const [seasonFilter, setSeasonFilter] = useState<SeasonFilter>('all');
    const [groupOption, setGroupOption] = useState<string>('all');

    const query = location.search;
    const params = useMemo(() => new URLSearchParams(query), [query]);

    const searchTermRaw = useMemo(() => (params.get(SEARCH_QUERY_KEY) || '').trim(), [params]);
    const searchTerm = useMemo(() => normalizeString(searchTermRaw), [searchTermRaw]);
    const searchAppliedProducts = useMemo(() => {
        if (searchTerm.length < 2) return sourceProducts;
        const tokens = splitToTokensNormalized(searchTerm);
        if (!tokens.length) return sourceProducts;
        const scored: { product: ProductItem; score: number }[] = [];
        for (const p of sourceProducts) {
            const score = scoreFieldsForSearch(
                {
                    name: p?.name || '',
                    description: p?.nameDescription || p?.description || '',
                    fillerType: (p?.fillerType && p.fillerType.name) || '',
                    fabricType: (p as any)?.fabricType?.name || '',
                    category: p?.category?.name || '',
                },
                tokens,
            );
            if (score > 0) {
                scored.push({ product: p, score });
            }
        }
        if (!scored.length) return [];
        scored.sort((a, b) => b.score - a.score);
        return scored.map((x) => x.product);
    }, [sourceProducts, searchTerm]);

    const {
        checkFilterItem: checkFillerFilterItem,
        checkedFilterItems: checkedFillerFilterItems,
        productsTorender: fillerTypeFilteredProducts,
        checkboxItems: fillerCheckboxItems,
    } = useFilterByFillerType(searchAppliedProducts, typesOfFiller);

    const {
        checkFilterItem: checkFabricFilterItem,
        checkedFilterItems: checkedFabricFilterItems,
        productsTorender: fabricTypeFilteredProducts,
        checkboxItems: fabricCheckboxItems,
    } = useFilterByFabricType(fillerTypeFilteredProducts, typesOfFabric);

    // --- FACET: доступные FILLER после выбранной FABRIC (без учёта выбранного FILLER) ---
    const productsAfterFabricOnly = useMemo(() => {
        if (checkedFabricFilterItems.length === 0) return searchAppliedProducts;
        const set = new Set(checkedFabricFilterItems.map(normalizeKey));
        return searchAppliedProducts.filter((p: any) => {
            const k = getFabricKey(p);
            return k ? set.has(k) : false;
        });
    }, [searchAppliedProducts, checkedFabricFilterItems]);
    const availableFillerAfterFabric = useMemo(() => {
        const set = new Set<string>();
        for (const p of productsAfterFabricOnly as any[]) {
            const k = getFillerKey(p);
            if (k) set.add(k);
        }
        return set;
    }, [productsAfterFabricOnly]);
    const fillerCheckboxItemsUi = useMemo(() => {
        if (checkedFabricFilterItems.length === 0) return fillerCheckboxItems;
        if (availableFillerAfterFabric.size === 0) return fillerCheckboxItems.filter((x) => x.checked);
        return fillerCheckboxItems.filter((x) => x.checked || availableFillerAfterFabric.has(normalizeKey(x.id)));
    }, [fillerCheckboxItems, availableFillerAfterFabric, checkedFabricFilterItems.length]);
    // --- доступные FABRIC после выбранного FILLER (без учёта выбранной FABRIC) ---
    const productsAfterFillerOnly = useMemo(() => {
        if (checkedFillerFilterItems.length === 0) return searchAppliedProducts;
        const set = new Set(checkedFillerFilterItems.map(normalizeKey));
        return searchAppliedProducts.filter((p: any) => {
            const k = getFillerKey(p);
            return k ? set.has(k) : false;
        });
    }, [searchAppliedProducts, checkedFillerFilterItems]);
    const availableFabricAfterFiller = useMemo(() => {
        const set = new Set<string>();
        for (const p of productsAfterFillerOnly as any[]) {
            const k = getFabricKey(p);
            if (k) set.add(k);
        }
        return set;
    }, [productsAfterFillerOnly]);
    const fabricCheckboxItemsUi = useMemo(() => {
        if (checkedFillerFilterItems.length === 0) return fabricCheckboxItems;
        if (availableFabricAfterFiller.size === 0) return fabricCheckboxItems.filter((x) => x.checked);
        return fabricCheckboxItems.filter((x) => x.checked || availableFabricAfterFiller.has(normalizeKey(x.id)));
    }, [fabricCheckboxItems, availableFabricAfterFiller, checkedFillerFilterItems.length]);

    const explicitSeasons = useMemo<SeasonFilter[]>(() => {
        const set = new Set<string>();
        fabricTypeFilteredProducts.forEach((p) => {
            if (p.season && typeof p.season === 'string') {
                set.add(p.season.toLowerCase());
            } else {
                set.add('seasonless');
            }
        });
        const mapped: SeasonFilter[] = [];
        if (set.has('seasonless')) mapped.push('seasonless');
        (['summer', 'autumn', 'winter', 'spring'] as const).forEach((s) => {
            if (set.has(s)) mapped.push(s);
        });
        return mapped;
    }, [fabricTypeFilteredProducts]);

    const hasMultipleSeasons = explicitSeasons.length > 1;

    const seasonOptions = useMemo(
        () =>
            hasMultipleSeasons
                ? (['all', ...explicitSeasons] as SeasonFilter[]).map((s) => ({
                      value: s,
                      label: seasonLabelMap[s],
                  }))
                : undefined,
        [explicitSeasons, hasMultipleSeasons],
    );

    const seasonAppliedProducts = useMemo(() => {
        if (seasonFilter === 'all' || !hasMultipleSeasons) return fabricTypeFilteredProducts;
        if (seasonFilter === 'seasonless') {
            return fabricTypeFilteredProducts.filter((p) => p.season == null);
        }
        return fabricTypeFilteredProducts.filter((p) => (p.season ?? '').toLowerCase() === seasonFilter);
    }, [fabricTypeFilteredProducts, seasonFilter, hasMultipleSeasons]);

    // const hasActiveSeasonOrFilters = seasonFilter !== 'all' || checkedFillerFilterItems.length > 0 || checkedFabricFilterItems.length > 0;

    // const groupMeta = useMemo(() => {
    //     if (!hasActiveSeasonOrFilters) return undefined;
    //     const items = seasonAppliedProducts;
    //     if (!items.length) return undefined;
    //     const norm = (s: string) => (s ?? '').trim().toLowerCase();
    //     const urls = new Set(items.map((p) => norm(p.group?.url ?? '')));
    //     if (urls.size !== 1) return undefined;
    //     const only = (urls.values().next().value as string) || '';
    //     if (!only) return undefined;
    //     const label = items.find((p) => norm(p.group?.url ?? '') === only)?.group?.url ?? '';
    //     const options = Array.from(new Set(items.map((p) => String(p.group?.optionValue ?? '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));
    //     if (!options.length) return undefined;
    //     return { label, options };
    // }, [seasonAppliedProducts, hasActiveSeasonOrFilters]);

    const groupMeta = useMemo(() => {
        const items = seasonAppliedProducts;
        if (!items.length) return undefined;
        const norm = (s: string) => (s ?? '').trim().toLowerCase();
        const urls = new Set(items.map((p) => norm(p.group?.url ?? '')).filter(Boolean));
        if (urls.size !== 1) return undefined;
        const only = urls.values().next().value as string;
        const label = items.find((p) => norm(p.group?.url ?? '') === only)?.group?.url ?? '';
        const options = Array.from(new Set(items.map((p) => String(p.group?.optionValue ?? '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));
        if (!options.length) return undefined;
        return { label, options };
    }, [seasonAppliedProducts]);

    const groupOptions = useMemo(() => groupMeta?.options.map((v) => ({ value: v, label: v })), [groupMeta]);
    const selectedSize = useMemo(() => params.get('size') || 'all', [params]);
    const selectedColor = useMemo(() => params.get('color') || 'all', [params]);

    const priceFromParam = useMemo(() => {
        const s = params.get('priceFrom');
        const n = s != null ? Number(s) : NaN;
        return Number.isFinite(n) ? n : null;
    }, [params]);

    const priceToParam = useMemo(() => {
        const s = params.get('priceTo');
        const n = s != null ? Number(s) : NaN;
        return Number.isFinite(n) ? n : null;
    }, [params]);

    const extractPrice = (p: ProductItem): number | null => {
        const cand = [
            p?.specialDiscount?.discountedPrice,
            p?.discount?.discountedPrice,
            p?.price,
            (p as any)?.currentPrice,
            (p as any)?.finalPrice,
            (p as any)?.discountPrice,
            (p as any)?.cost,
        ];
        for (const v of cand) {
            if (typeof v === 'number' && Number.isFinite(v) && v >= 0) return v;
            if (typeof v === 'string' && v.trim() !== '' && !isNaN(+v)) return +v;
        }
        return null;
    };

    const sizeColorFilteredProducts = useMemo(() => {
        if (selectedSize === 'all' && selectedColor === 'all') return seasonAppliedProducts;
        return seasonAppliedProducts.filter((p) => {
            const passSize = selectedSize === 'all' ? true : p?.group?.url === 'Размер' && String(p?.group?.optionValue ?? '') === selectedSize;
            const passColor = selectedColor === 'all' ? true : p?.group?.url === 'Цвет' && String(p?.group?.optionValue ?? '') === selectedColor;
            return passSize && passColor;
        });
    }, [seasonAppliedProducts, selectedSize, selectedColor]);

    const groupFilteredProducts = useMemo(() => {
        if (!groupOptions || groupOption === 'all') return sizeColorFilteredProducts;
        return sizeColorFilteredProducts.filter((p) => String(p.group?.optionValue ?? '').trim() === groupOption);
    }, [sizeColorFilteredProducts, groupOptions, groupOption]);

    const priceFilteredProducts = useMemo(() => {
        const hasFrom = priceFromParam != null;
        const hasTo = priceToParam != null;
        if (!hasFrom && !hasTo) return groupFilteredProducts;
        const low = hasFrom ? (priceToParam != null ? Math.min(priceFromParam!, priceToParam!) : priceFromParam!) : -Infinity;
        const high = hasTo ? (priceFromParam != null ? Math.max(priceFromParam!, priceToParam!) : priceToParam!) : Infinity;
        return groupFilteredProducts.filter((p) => {
            const priced = extractPrice(p);
            if (priced == null) return false;
            return priced >= low && priced <= high;
        });
    }, [groupFilteredProducts, priceFromParam, priceToParam]);

    // --- ПРИМЕНЕНИЕ СКРЫТОГО ФИЛЬТРА ПО ЦЕНЕ ---
    const secretMinPriceKey = useMemo(() => {
        const raw = params.get(SECRET_MIN_PRICE_QUERY_KEY);
        return raw ? raw.trim() : null;
    }, [params]);
    const secretMinPrice = useMemo(() => {
        if (!secretMinPriceKey) return null;
        const value = SECRET_MIN_PRICE_MAP[secretMinPriceKey];
        if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return null;
        return value;
    }, [secretMinPriceKey]);

    const hiddenPriceFilteredProducts = useMemo(() => {
        if (secretMinPrice == null) return priceFilteredProducts;
        return priceFilteredProducts.filter((p) => {
            const priced = extractPrice(p);
            if (priced == null) return false;
            return priced >= secretMinPrice;
        });
    }, [priceFilteredProducts, secretMinPrice]);

    const fullPriceOnly = useMemo(() => {
        const raw = params.get(FULL_PRICE_QUERY_KEY);
        if (!raw) return false;
        const normalized = raw.trim().toLowerCase();
        return normalized === '1' || normalized === 'true' || normalized === 'yes';
    }, [params]);

    const fullPriceFilteredProducts = useMemo(() => {
        if (!fullPriceOnly) return hiddenPriceFilteredProducts;
        return hiddenPriceFilteredProducts.filter((p) => {
            const hasSpecialDiscount = typeof p?.specialDiscount?.discountedPrice === 'number' && Number.isFinite(p.specialDiscount!.discountedPrice!);
            const hasRegularDiscount = typeof p?.discount?.discountedPrice === 'number' && Number.isFinite(p.discount!.discountedPrice!);
            return !hasSpecialDiscount && !hasRegularDiscount;
        });
    }, [hiddenPriceFilteredProducts, fullPriceOnly]);

    const finalProductsToRender = useMemo(() => fullPriceFilteredProducts, [fullPriceFilteredProducts]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        // --- fillerType из URL ---
        const fillerParam = params.get(FILTER_QUERY_KEY);
        const fillerIdsFromUrl = fillerParam ? fillerParam.split(',').filter(Boolean) : [];
        fillerIdsFromUrl.forEach((id) => {
            if (!checkedFillerFilterItems.includes(id)) checkFillerFilterItem(id);
        });
        checkedFillerFilterItems.forEach((id) => {
            if (!fillerIdsFromUrl.includes(id)) checkFillerFilterItem(id);
        });

        // --- fabricType из URL ---
        const fabricParam = params.get(FILTER_QUERY_KEY_FABRIC);
        const fabricIdsFromUrl = fabricParam ? fabricParam.split(',').filter(Boolean) : [];
        fabricIdsFromUrl.forEach((id) => {
            if (!checkedFabricFilterItems.includes(id)) checkFabricFilterItem(id);
        });
        checkedFabricFilterItems.forEach((id) => {
            if (!fabricIdsFromUrl.includes(id)) checkFabricFilterItem(id);
        });

        const seasonParam = params.get('season');
        let normalizedSeason: SeasonFilter = 'all';
        if (seasonParam) {
            const lower = seasonParam.toLowerCase();
            if (lower === 'seasonless') normalizedSeason = 'seasonless';
            else if (['summer', 'autumn', 'winter', 'spring'].includes(lower)) {
                normalizedSeason = lower as SeasonFilter;
            }
        }
        if (!hasMultipleSeasons) {
            normalizedSeason = 'all';
        } else if (normalizedSeason !== 'all' && !explicitSeasons.includes(normalizedSeason)) {
            normalizedSeason = 'all';
        }
        if (normalizedSeason !== seasonFilter) setSeasonFilter(normalizedSeason);

        const groupParam = params.get(GROUP_QUERY_KEY);
        let normalizedGroup = 'all';
        if (groupParam && groupOptions?.some((o) => o.value === groupParam)) {
            normalizedGroup = groupParam;
        }
        if (!groupOptions) {
            normalizedGroup = 'all';
        }
        if (normalizedGroup !== groupOption) setGroupOption(normalizedGroup);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, explicitSeasons, hasMultipleSeasons, groupOptions, checkedFillerFilterItems, checkedFabricFilterItems]);

    useEffect(() => {
        const savedScrollPosition = sessionStorage.getItem('scrollPosition');
        if (savedScrollPosition) {
            window.scrollTo(0, parseInt(savedScrollPosition, 10));
        }
        return () => {
            sessionStorage.setItem('scrollPosition', window.scrollY.toString());
        };
    }, []);

    const basePath = url ? `${PATHS.showcase}/${url}` : location.pathname;

    const handleCheckFiller = (id: string) => {
        const isActive = checkedFillerFilterItems.includes(id);
        checkFillerFilterItem(id);
        const newChecked = isActive ? checkedFillerFilterItems.filter((x) => x !== id) : [...checkedFillerFilterItems, id];

        const params = new URLSearchParams(location.search);

        newChecked.length ? params.set(FILTER_QUERY_KEY, newChecked.join(',')) : params.delete(FILTER_QUERY_KEY);

        // сохранить fabric-фильтр в URL
        if (checkedFabricFilterItems.length) params.set(FILTER_QUERY_KEY_FABRIC, checkedFabricFilterItems.join(','));
        else params.delete(FILTER_QUERY_KEY_FABRIC);

        if (hasMultipleSeasons && seasonFilter !== 'all') params.set('season', seasonFilter);
        else params.delete('season');
        if (groupOptions && groupOption !== 'all') params.set(GROUP_QUERY_KEY, groupOption);
        else params.delete(GROUP_QUERY_KEY);

        const search = params.toString();
        navigate(`${basePath}${search ? `?${search}` : ''}`, { replace: false });
    };

    const handleCheckFabric = (id: string) => {
        const isActive = checkedFabricFilterItems.includes(id);
        checkFabricFilterItem(id);
        const newChecked = isActive ? checkedFabricFilterItems.filter((x) => x !== id) : [...checkedFabricFilterItems, id];

        const params = new URLSearchParams(location.search);

        // сохранить filler-фильтр в URL
        if (checkedFillerFilterItems.length) params.set(FILTER_QUERY_KEY, checkedFillerFilterItems.join(','));
        else params.delete(FILTER_QUERY_KEY);

        newChecked.length ? params.set(FILTER_QUERY_KEY_FABRIC, newChecked.join(',')) : params.delete(FILTER_QUERY_KEY_FABRIC);

        if (hasMultipleSeasons && seasonFilter !== 'all') params.set('season', seasonFilter);
        else params.delete('season');
        if (groupOptions && groupOption !== 'all') params.set(GROUP_QUERY_KEY, groupOption);
        else params.delete(GROUP_QUERY_KEY);

        const search = params.toString();
        navigate(`${basePath}${search ? `?${search}` : ''}`, { replace: false });
    };

    const handleSeasonChange = (season: SeasonFilter) => {
        if (!hasMultipleSeasons) return;
        setSeasonFilter(season);
        const params = new URLSearchParams(location.search);

        if (checkedFillerFilterItems.length) params.set(FILTER_QUERY_KEY, checkedFillerFilterItems.join(','));
        else params.delete(FILTER_QUERY_KEY);

        if (checkedFabricFilterItems.length) params.set(FILTER_QUERY_KEY_FABRIC, checkedFabricFilterItems.join(','));
        else params.delete(FILTER_QUERY_KEY_FABRIC);

        if (season !== 'all') params.set('season', season);
        else params.delete('season');
        if (groupOptions && groupOption !== 'all') params.set(GROUP_QUERY_KEY, groupOption);
        else params.delete(GROUP_QUERY_KEY);
        const search = params.toString();
        navigate(`${basePath}${search ? `?${search}` : ''}`, { replace: false });
    };

    const handleGroupOptionChange = (value: string) => {
        if (!groupOptions) return;
        const valid = value === 'all' || groupOptions.some((o) => o.value === value);
        const next = valid ? value : 'all';
        setGroupOption(next);
        const params = new URLSearchParams(location.search);

        if (checkedFillerFilterItems.length) params.set(FILTER_QUERY_KEY, checkedFillerFilterItems.join(','));
        else params.delete(FILTER_QUERY_KEY);

        if (checkedFabricFilterItems.length) params.set(FILTER_QUERY_KEY_FABRIC, checkedFabricFilterItems.join(','));
        else params.delete(FILTER_QUERY_KEY_FABRIC);

        if (hasMultipleSeasons && seasonFilter !== 'all') params.set('season', seasonFilter);
        else params.delete('season');
        if (next !== 'all') params.set(GROUP_QUERY_KEY, next);
        else params.delete(GROUP_QUERY_KEY);
        const search = params.toString();
        navigate(`${basePath}${search ? `?${search}` : ''}`, { replace: false });
    };

    const selectedFilterChips: SelectedFilterChip[] = [];
    if (hasMultipleSeasons && seasonFilter !== 'all') {
        selectedFilterChips.push({
            key: `season-${seasonFilter}`,
            label: seasonLabelMap[seasonFilter],
            onRemove: () => handleSeasonChange('all'),
        });
    }
    const fabricNameById = new Map(checkboxItemsFromArray(fabricCheckboxItems));
    checkedFabricFilterItems.forEach((id) => {
        const label = fabricNameById.get(normalizeKey(id));
        if (!label) return;
        selectedFilterChips.push({
            key: `fabric-${id}`,
            label,
            onRemove: () => handleCheckFabric(id),
        });
    });
    const fillerNameById = new Map(checkboxItemsFromArray(fillerCheckboxItems));
    checkedFillerFilterItems.forEach((id) => {
        const label = fillerNameById.get(normalizeKey(id));
        if (!label) return;
        selectedFilterChips.push({
            key: `filler-${id}`,
            label,
            onRemove: () => handleCheckFiller(id),
        });
    });
    if (groupOptions && groupOption !== 'all') {
        const selectedGroupLabel = groupOptions.find((item) => item.value === groupOption)?.label ?? groupOption;
        selectedFilterChips.push({
            key: `group-${groupOption}`,
            label: selectedGroupLabel,
            onRemove: () => handleGroupOptionChange('all'),
        });
    }

    const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
        const showcasePath = PATHS.showcase || '/catalog';
        const items: BreadcrumbItem[] = [
            { label: 'Главная', to: '/' },
            { label: 'Все товары', to: showcasePath },
        ];
        // /catalog/discounts
        if (isDiscountsRoute) {
            items.push({ label: 'Акции' });
            return items;
        }
        // /catalog/:url (категория)
        if (category?.name && url) {
            items.push({ label: category.name });
            return items;
        }
        // /catalog?q=...
        if (searchTermRaw) {
            items.push({ label: `Поиск: ${searchTermRaw}` });
        }
        return items;
    }, [category?.name, isDiscountsRoute, searchTermRaw, url]);

    if (notFound) return <NotFound />;

    const headerTitle = searchTermRaw ? `Результаты по «${searchTermRaw}»` : isDiscountsRoute ? 'Акции' : url ? (category?.name ?? '') : 'Все товары';
    const emptyText = searchTermRaw ? `По запросу «${searchTermRaw}» ничего не найдено` : url && !isDiscountsRoute ? NO_PRODUCTS_MESSAGE : NO_PRODUCTS;

    return (
        <Section>
            <>
                <div className={classes.headerWrapper}>
                    <Breadcrumbs items={breadcrumbItems} />
                    <SectionHeader title={headerTitle} />
                </div>

                <SectionBody>
                    <SectionBodyGrid>
                        <>
                            {isMobileFilter ? (
                                <MobileFilter
                                    checkboxItemsFiller={fillerCheckboxItemsUi}
                                    onCheck={handleCheckFiller}
                                    checkboxItemsFabric={fabricCheckboxItemsUi}
                                    onCheckFabric={handleCheckFabric}
                                    currentSeason={seasonFilter}
                                    onSeasonChange={handleSeasonChange}
                                    seasonOptions={seasonOptions}
                                    groupFilterLabel={groupMeta?.label}
                                    groupFilterOptions={groupOptions}
                                    currentGroupOption={groupOption}
                                    onGroupOptionChange={handleGroupOptionChange}
                                />
                            ) : (
                                <Filter
                                    checkboxItemsFiller={fillerCheckboxItemsUi}
                                    onCheck={handleCheckFiller}
                                    checkboxItemsFabric={fabricCheckboxItemsUi}
                                    onCheckFabric={handleCheckFabric}
                                    currentSeason={seasonFilter}
                                    onSeasonChange={handleSeasonChange}
                                    seasonOptions={seasonOptions}
                                    groupFilterLabel={groupMeta?.label}
                                    groupFilterOptions={groupOptions}
                                    currentGroupOption={groupOption}
                                    onGroupOptionChange={handleGroupOptionChange}
                                />
                            )}

                            {error?.isError && <Placeholder text={error.message} size={'38px'} />}

                            {!hasProducts && !error?.isError && <Placeholder text={emptyText} size={'38px'} />}

                            {hasProducts && (
                                <ProductCardList
                                    isMobile={isMobile}
                                    products={finalProductsToRender}
                                    currentSeason={seasonFilter === 'seasonless' ? 'all' : seasonFilter}
                                    selectedFilterChips={isMobileFilter ? [] : selectedFilterChips}
                                />
                            )}
                        </>
                    </SectionBodyGrid>
                </SectionBody>
            </>
        </Section>
    );
};

export default CatalogPage;
